export const queryKeys = {
  // Organizations domain
  organizations: {
    all: ['organizations'] as const,
    lists: () => [...queryKeys.organizations.all, 'list'] as const,
    list: (filters: any) =>
      [...queryKeys.organizations.lists(), filters] as const,
    details: () => [...queryKeys.organizations.all, 'detail'] as const,
    // Route params are strings but API records carry numeric ids; one key form for both.
    detail: (id: string | number) =>
      [...queryKeys.organizations.details(), String(id)] as const,
    settings: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'settings'] as const,
    sensors: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'sensors'] as const,
    aiIntegrations: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'ai-integrations'] as const,
    aiIntegrationUsage: (
      id: string | number,
      saId: string | number,
      start: string,
      end: string,
      user: string,
      team: string,
      granularity: string
    ) =>
      [
        ...queryKeys.organizations.detail(id),
        'ai-integration-usage',
        saId,
        start,
        end,
        user,
        team,
        granularity
      ] as const,
    aiIntegrationExplorer: (
      id: string | number,
      saId: string | number,
      start: string,
      end: string
    ) =>
      [
        ...queryKeys.organizations.detail(id),
        'ai-integration-explorer',
        saId,
        start,
        end
      ] as const,
    aiBackfillResult: (
      id: string | number,
      saId: string | number,
      jobsSuiteUuid: string
    ) =>
      [
        ...queryKeys.organizations.detail(id),
        'ai-backfill-result',
        saId,
        jobsSuiteUuid
      ] as const,
    aiBackfillHistory: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'ai-backfill-history'] as const,
    aiChatHistory: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'ai-chat-history'] as const,
    aiChatCount: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'ai-chat-count'] as const,
    aiChatSession: (id: string | number, sessionKey: string) =>
      [
        ...queryKeys.organizations.detail(id),
        'ai-chat-session',
        sessionKey
      ] as const,
    agenticChatHistory: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'ai-chat-history-v2'] as const,
    agenticChatSession: (id: string | number, sessionId: string) =>
      [
        ...queryKeys.organizations.detail(id),
        'ai-chat-session-v2',
        sessionId
      ] as const,
    customDashboards: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'custom-dashboards'] as const,
    accounts: (id: string | number, search: string, adminsOnly: boolean) =>
      [
        ...queryKeys.organizations.detail(id),
        'accounts',
        search,
        adminsOnly
      ] as const,
    customDashboard: (id: string | number, dashboardId: string | number) =>
      [
        ...queryKeys.organizations.detail(id),
        'custom-dashboards',
        dashboardId
      ] as const,
    customDashboardWidgetExec: (
      id: string | number,
      dashboardId: string | number,
      widgetId: string,
      parameters: Record<string, string>,
      targetOrgId?: string
    ) =>
      [
        ...queryKeys.organizations.detail(id),
        'custom-dashboards',
        dashboardId,
        'widget-exec',
        widgetId,
        parameters,
        targetOrgId
      ] as const,
    recentPrs: (id: string | number, filters?: any) =>
      [
        ...queryKeys.organizations.detail(id),
        'recent_prs',
        ...(filters ? [filters] : [])
      ] as const,
    insights: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'insights'] as const,
    hasInsights: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'has-insights'] as const,
    buyers: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'buyers'] as const,
    healthScore: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'health-score'] as const,
    invoices: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'invoices'] as const,
    usage: (id: string | number) =>
      [...queryKeys.organizations.detail(id), 'usage'] as const
  },

  // OPAs (On-Premise Apps) domain
  opas: {
    all: ['opas'] as const,
    lists: () => [...queryKeys.opas.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.opas.lists(), filters] as const,
    jfrogKeys: () => [...queryKeys.opas.all, 'jfrog-keys'] as const
  },

  // Projects domain (file-based config editor)
  projects: {
    all: ['projects'] as const,
    config: (orgId: string | number) =>
      [...queryKeys.projects.all, 'config', orgId] as const
  },

  // PDT projects domain (pdt.projects-backed org-details section)
  pdtProjects: {
    all: ['pdtProjects'] as const,
    list: (orgId: string | number, params?: unknown) =>
      [...queryKeys.pdtProjects.all, 'list', orgId, params ?? {}] as const,
    runs: (orgId: string | number) =>
      [...queryKeys.pdtProjects.all, 'runs', orgId] as const
  },

  // Users domain
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.users.lists(), filters] as const
  },

  // Changelog domain
  changelog: {
    all: ['changelog'] as const,
    content: () => [...queryKeys.changelog.all, 'content'] as const
  },

  // Zendesk domain
  zendesk: {
    all: ['zendesk'] as const,
    tickets: (orgId: string | number) =>
      [...queryKeys.zendesk.all, 'tickets', orgId] as const,
    summary: (orgId: string | number) =>
      [...queryKeys.zendesk.all, 'summary', orgId] as const
  },

  // Meter domain
  meter: {
    all: ['meter'] as const,
    balance: (orgId: string | number) =>
      [...queryKeys.meter.all, 'balance', orgId] as const
  },

  // Feature Requests domain
  featureRequests: {
    all: ['featureRequests'] as const,
    lists: () => [...queryKeys.featureRequests.all, 'list'] as const,
    list: (filters: any) =>
      [...queryKeys.featureRequests.lists(), filters] as const,
    byOrg: (orgId: string | number, filters?: any) =>
      [
        ...queryKeys.featureRequests.all,
        'by-org',
        orgId,
        ...(filters ? [filters] : [])
      ] as const
  },

  // Git Org Search domain
  gitOrgSearch: {
    all: ['gitOrgSearch'] as const,
    lists: () => [...queryKeys.gitOrgSearch.all, 'list'] as const,
    list: (filters: any) =>
      [...queryKeys.gitOrgSearch.lists(), filters] as const
  },

  // User Org Search domain
  userOrgSearch: {
    all: ['userOrgSearch'] as const,
    lists: () => [...queryKeys.userOrgSearch.all, 'list'] as const,
    list: (filters: any) =>
      [...queryKeys.userOrgSearch.lists(), filters] as const
  },

  // SFDC (Salesforce) domain
  sfdc: {
    all: ['sfdc'] as const,
    account: (orgId: string | number) =>
      [...queryKeys.sfdc.all, 'account', orgId] as const,
    csmBulk: (orgIds: string[]) =>
      [...queryKeys.sfdc.all, 'csm-bulk', orgIds.join(',')] as const,
    csmSummary: () => [...queryKeys.sfdc.all, 'csm-summary'] as const
  },

  // Feature Flags domain
  featureFlags: {
    all: ['featureFlags'] as const,
    list: () => [...queryKeys.featureFlags.all, 'list'] as const,
    organizations: (definitionId: number) =>
      [...queryKeys.featureFlags.all, 'organizations', definitionId] as const,
    sync: () => [...queryKeys.featureFlags.all, 'sync'] as const
  },

  // Multi-Org domain
  multiOrg: {
    all: ['multiOrg'] as const,
    lists: () => [...queryKeys.multiOrg.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.multiOrg.all, 'detail', id] as const
  },

  // Deployed app version (new-release prompt)
  appVersion: {
    all: ['appVersion'] as const,
    latest: () => [...queryKeys.appVersion.all, 'latest'] as const
  }
}
